<template>
  <div
    class="absolute"
    :style="pipStyle"
    data-pip
    @mousedown="emit('dragStart', $event)"
    @click.stop="emit('click', item)"
  >
    <!-- Main pip circle -->
    <div
      class="w-full h-full rounded-full transition-all border-2"
      :class="[
        isSelected ? 'border-white shadow-lg shadow-white/50 scale-110' : 'border-gray-800',
        !isOn && 'opacity-50',
        'cursor-grab active:cursor-grabbing hover:scale-105'
      ]"
      :style="{ backgroundColor: pipColor }"
    >
      <!-- Inheritance indicator -->
      <div
        v-if="isInherited"
        class="absolute -inset-1 rounded-full border-2 border-dashed border-gray-400 opacity-60"
      />
    </div>
    
    <!-- Inherited label -->
    <div
      v-if="isInherited && inheritedFrom"
      class="absolute top-full mt-1 left-1/2 -translate-x-1/2 text-[10px] text-gray-400 whitespace-nowrap px-1.5 py-0.5 bg-gray-900/80 rounded"
    >
      {{ inheritedFrom }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Light } from '@/stores/lights'
import { hsvToRGB, hueStateToHSV, ctToRGB } from '@/lib/colorConversion'
import { getPipStyle } from '@/lib/coordinateUtils'

interface Props {
  item: Light
  x: number
  y: number
  circleRadius: number
  circleCenterX: number
  circleCenterY: number
  isSelected?: boolean
  isInherited?: boolean
  inheritedFrom?: string
  size?: number
}

const props = withDefaults(defineProps<Props>(), {
  isSelected: false,
  isInherited: false,
  size: 24
})

const emit = defineEmits<{
  dragStart: [event: MouseEvent]
  click: [item: Light]
}>()

const isOn = computed(() => props.item.current_state?.on ?? true)

const pipStyle = computed(() => {
  const style = getPipStyle(
    { x: props.x, y: props.y },
    {
      centerX: props.circleCenterX,
      centerY: props.circleCenterY,
      radius: props.circleRadius
    },
    props.size
  )
  
  return {
    ...style,
    zIndex: props.isSelected ? 30 : 20
  }
})

const pipColor = computed(() => {
  const state = props.item.current_state
  
  if (state?.hue != null && state?.sat != null) {
    const hsv = hueStateToHSV(state.hue, state.sat)
    const rgb = hsvToRGB(hsv)
    return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
  } else if (state?.ct != null) {
    const rgb = ctToRGB(state.ct)
    return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
  }
  
  return '#888'
})
</script>
